"use client";

import { type ReactNode, useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import { DilutionAnswer } from "@/components/tools/chemistry/dilution-answer";
import { QuantityInput } from "@/components/tools/chemistry/quantity-input";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { dilute } from "@/lib/tool-kit/chemistry/dilution";
import { fewestSigFigs } from "@/lib/tool-kit/chemistry/format";
import type { DilutionTerm } from "@/lib/tool-kit/chemistry/solutions";
import {
  CONCENTRATION_UNITS,
  unitOptions,
  VOLUME,
} from "@/lib/tool-kit/chemistry/units";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const DILUTION_CALCULATOR_STRINGS = {
  solveLabel: "Solve for",
  solveV1: "Stock volume (V₁)",
  solveC1: "Stock concentration (C₁)",
  solveC2: "Final concentration (C₂)",
  solveV2: "Final volume (V₂)",
  labelC1: "Stock concentration (C₁)",
  labelV1: "Stock volume to take (V₁)",
  labelC2: "Final concentration (C₂)",
  labelV2: "Final volume (V₂)",
  answerSuffix: "{label} (answer)",
  concUnit: "Concentration unit",
  volumeUnit: "Volume unit",
  sigFigs: "Significant figures in the answer",
  sigMatch: "Match my data",
  sigChoice:
    "one: {count} significant figure|other: {count} significant figures",
  results: "Results",
  answerC1: "Stock concentration (C₁)",
  answerV1: "Stock volume (V₁)",
  answerC2: "Final concentration (C₂)",
  answerV2: "Final volume (V₂)",
  dilutionFactor: "Dilution factor (C₁ ÷ C₂)",
  diluent: "Diluent to add (V₂ − V₁)",
  working: "The working",
  equation: "C₁V₁ = C₂V₂",
  rearrangedC1: "C₁ = C₂V₂ ÷ V₁",
  rearrangedV1: "V₁ = C₂V₂ ÷ C₁",
  rearrangedC2: "C₂ = C₁V₁ ÷ V₂",
  rearrangedV2: "V₂ = C₁V₁ ÷ C₂",
  substitutedC1:
    "C₁ = ({c2} {cUnit} × {v2} {vUnit}) ÷ {v1} {vUnit} = {result} {cUnit}",
  substitutedV1:
    "V₁ = ({c2} {cUnit} × {v2} {vUnit}) ÷ {c1} {cUnit} = {result} {vUnit}",
  substitutedC2:
    "C₂ = ({c1} {cUnit} × {v1} {vUnit}) ÷ {v2} {vUnit} = {result} {cUnit}",
  substitutedV2:
    "V₂ = ({c1} {cUnit} × {v1} {vUnit}) ÷ {c2} {cUnit} = {result} {vUnit}",
  convertLine: "{value} {unit} = {converted} {target}",
  factorLine: "Dilution factor = {c1} ÷ {c2} = {factor}",
  recipeHeading: "How to make it",
  recipe:
    "Measure {v1} {vUnit} of the stock solution and add {diluent} {vUnit} of diluent, for a final volume of {v2} {vUnit}. That is 1 part stock to {parts} parts diluent. For accurate work, put the stock in a volumetric flask and add diluent up to the mark rather than adding a measured volume of diluent; the two volumes do not always add exactly.",
  concentrates:
    "The final concentration is higher than the stock's, so this is a concentration step, not a dilution. Check which solution is the stock.",
  stockTooLarge:
    "The stock volume is larger than the final volume, which cannot be made by dilution. Check the units and the numbers.",
  enterValues: "Enter the other three quantities to see the answer.",
  invalid: "Each quantity must be a number greater than zero.",
  unitMismatch:
    "C₁ and C₂ are in different kinds of unit (for example mol/L and g/L). Dilution needs the same kind; convert one using the molar mass first.",
  copy: "Copy answer",
  copied: "Copied!",
  copyText: "{label}: {value}",
} as const;

const SIG_CHOICES = ["3", "4", "5", "6"] as const;
const DEFAULT_SIG = 4;
const CONC_OPTIONS = CONCENTRATION_UNITS.map((u) => ({
  value: u.id,
  label: u.symbol,
}));
const VOLUME_OPTIONS = unitOptions(VOLUME).filter((u) =>
  ["uL", "mL", "L"].includes(u.value)
);

interface Quantity {
  text: string;
  unit: string;
}

const START: Record<DilutionTerm, Quantity> = {
  c1: { text: "12.0", unit: "M" },
  v1: { text: "", unit: "mL" },
  c2: { text: "1.00", unit: "M" },
  v2: { text: "500.0", unit: "mL" },
};

function readValue(text: string): number | null {
  return text.trim() === "" ? null : parseMeasuredNumber(text);
}

export default function DilutionCalculatorWidget() {
  const strings = useToolUi(DILUTION_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [unknown, setUnknown] = useState<DilutionTerm>("v1");
  const [q, setQ] = useState<Record<DilutionTerm, Quantity>>(START);
  const [sigChoice, setSigChoice] = useState("match");
  const set = (term: DilutionTerm, patch: Partial<Quantity>) =>
    setQ({ ...q, [term]: { ...q[term], ...patch } });

  const outcome = dilute({
    unknown,
    c1: readValue(q.c1.text),
    v1: readValue(q.v1.text),
    c2: readValue(q.c2.text),
    v2: readValue(q.v2.text),
    c1Unit: q.c1.unit,
    c2Unit: q.c2.unit,
    v1Unit: q.v1.unit,
    v2Unit: q.v2.unit,
  });
  const entries = (["c1", "v1", "c2", "v2"] as const)
    .filter((term) => term !== unknown)
    .map((term) => q[term].text);
  const sig =
    sigChoice === "match"
      ? fewestSigFigs(entries, DEFAULT_SIG)
      : Number(sigChoice);

  let body: ReactNode;
  if (outcome.ok) {
    body = (
      <DilutionAnswer
        r={outcome}
        sig={sig}
        strings={strings}
        texts={{ c1: q.c1.text, v1: q.v1.text, c2: q.c2.text, v2: q.v2.text }}
        typed={{ c2: readValue(q.c2.text), v1: readValue(q.v1.text) }}
        units={{ c1: q.c1.unit, c2: q.c2.unit, v1: q.v1.unit, v2: q.v2.unit }}
        unknown={unknown}
      />
    );
  } else {
    const message = {
      missing: strings.enterValues,
      invalid: strings.invalid,
      unitMismatch: strings.unitMismatch,
    }[outcome.reason];
    body =
      outcome.reason === "missing" ? (
        <Notice>{message}</Notice>
      ) : (
        <ErrorNotice>{message}</ErrorNotice>
      );
  }

  const field = (
    term: DilutionTerm,
    label: string,
    kind: "conc" | "volume"
  ) => (
    <QuantityInput
      id={`dil-${term}`}
      label={unknown === term ? t(strings.answerSuffix, { label }) : label}
      onUnitChange={(unit) => set(term, { unit })}
      onValueChange={(text) => set(term, { text })}
      solved={unknown === term}
      unit={q[term].unit}
      unitLabel={kind === "conc" ? strings.concUnit : strings.volumeUnit}
      units={kind === "conc" ? CONC_OPTIONS : VOLUME_OPTIONS}
      value={q[term].text}
    />
  );

  return (
    <>
      <PillGroup
        label={strings.solveLabel}
        name="dil-solve"
        onChange={setUnknown}
        options={[
          { value: "v1", label: strings.solveV1 },
          { value: "c1", label: strings.solveC1 },
          { value: "c2", label: strings.solveC2 },
          { value: "v2", label: strings.solveV2 },
        ]}
        value={unknown}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {field("c1", strings.labelC1, "conc")}
        {field("v1", strings.labelV1, "volume")}
        {field("c2", strings.labelC2, "conc")}
        {field("v2", strings.labelV2, "volume")}
        <SelectField
          id="dil-sig"
          label={strings.sigFigs}
          onChange={setSigChoice}
          options={[
            { value: "match", label: strings.sigMatch },
            ...SIG_CHOICES.map((value) => ({
              value,
              label: tPlural(strings.sigChoice, locale, Number(value), {
                count: fmt(Number(value)),
              }).trim(),
            })),
          ]}
          value={sigChoice}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}
